import { useState, useCallback } from 'react';
import { Link, Head } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell from '../../components/layout/TeacherShell';
import { Card } from '../../components/ui';
import {
  FrequencyBadge,
  TierBadge,
  PosBadge,
  MorphemeBreakdown,
  WordFamilyTree,
  DefinitionList,
  DefinitionLevelToggle,
  availableDefinitionLevels,
  WordSearch
} from '../../components/dictionary';
import { CreativeCommons } from 'lucide-react';
import { cn } from '../../lib/cn';

export default function DictionaryShow({ word, teacherCourseLists }) {
  const { t } = useTranslation();
  const [defLevel, setDefLevel] = useState('high');
  const availableLevels = availableDefinitionLevels(word.definitionGroups);

  // Inline jump-to-word search so a teacher can hop straight to another word
  // without bouncing back to the dictionary index. Same endpoint + result
  // shape the index uses; WordSearch's dropdown links to /teacher/dictionary/:id.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await fetch(`/teacher/dictionary/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data.data || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  return (
    <TeacherShell scrollTitle={<><Link href="/teacher/dictionary" className="hover:text-blue-600 dark:hover:text-blue-400">{t('dictionary.title')}</Link>{'  ›  '}{word.lemma}</>}>
      <Head title={`${word.lemma} - ${t('dictionary.title')}`} />

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link
          href="/teacher/dictionary"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('dictionary.backToDictionary')}
        </Link>

        {/* Jump-to-word search — sits above the word card so a teacher can
            navigate straight to another entry without returning to the index. */}
        <div className="mb-6">
          <WordSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            results={searchResults}
            isLoading={isSearching}
          />
        </div>

        {/* Main Card */}
        <Card className="p-8 mb-8">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                {/* POS sits with the headword, not in the badge cluster: it
                    identifies WHICH entry this is (a Word is lemma + pos),
                    where frequency and tier describe the word generally. */}
                <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mb-2">
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                    {word.lemma}
                  </h1>
                  <PosBadge pos={word.pos} size="lg" />
                </div>
                {word.ipa && (
                  <p className="text-lg text-gray-500 dark:text-gray-400 font-mono">
                    /{word.ipa}/
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <FrequencyBadge band={word.frequencyBand} frequencyZipf={word.frequencyZipf} size="lg" />
                <TierBadge tier={word.beckTier} size="lg" showTooltip />
              </div>
            </div>
            {teacherCourseLists && teacherCourseLists.length > 0 && (
              <div className="mt-4">
                <AddToCourseListButton word={word} teacherCourseLists={teacherCourseLists} />
              </div>
            )}
            {word.spellingVariantOf && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Spelling variant of{' '}
                <Link
                  href={`/teacher/dictionary/${word.spellingVariantOf.id}`}
                  className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline underline-offset-2"
                >
                  {word.spellingVariantOf.lemma}
                </Link>
              </p>
            )}
          </header>

          {/* Definitions */}
          <Section
            title={t('dictionary.definitions')}
            action={
              word.definitionGroups?.length ? (
                <DefinitionLevelToggle
                  value={defLevel}
                  onChange={setDefLevel}
                  available={availableLevels}
                />
              ) : null
            }
          >
            <DefinitionList
              definitions={word.definitions}
              groups={word.definitionGroups}
              level={defLevel}
              showSource
            />
          </Section>

          {/* Example Sentences — read-only for now; edit/flag/preferred
              controls are a follow-on slice. See vocabulary spec §6. */}
          {word.exampleSentences && word.exampleSentences.length > 0 && (
            <Section title={t('dictionary.exampleSentences')}>
              <ExampleSentenceList sentences={word.exampleSentences} />
            </Section>
          )}

          {/* Morpheme Breakdown */}
          {word.morphemes && word.morphemes.length > 0 && (
            <Section title={t('dictionary.morphemeBreakdown')}>
              <MorphemeBreakdown morphemes={word.morphemes} showMeaning />
            </Section>
          )}

          {/* Word Family */}
          {word.family && (
            <Section title={t('dictionary.wordFamily')}>
              <WordFamilyTree family={word.family} />
            </Section>
          )}

          {/* Related Words */}
          {(word.synonyms?.length > 0 || word.antonyms?.length > 0) && (
            <Section title={t('dictionary.relatedWords')}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {word.synonyms?.length > 0 && (
                  <RelatedWordsSection
                    title={t('dictionary.synonyms')}
                    words={word.synonyms}
                    colorClass="bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                  />
                )}
                {word.antonyms?.length > 0 && (
                  <RelatedWordsSection
                    title={t('dictionary.antonyms')}
                    words={word.antonyms}
                    colorClass="bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                  />
                )}
              </div>
            </Section>
          )}
        </Card>

        {/* Quick Actions */}
        <div className="flex justify-center gap-4">
          <Link
            href="/teacher/vocabulary_lists"
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('dictionary.viewVocabLists')}
          </Link>
        </div>

        {/* Attribution */}
        <div className="flex flex-col items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mt-8">
          <p className="flex items-center gap-1.5">
            <CreativeCommons className="w-3.5 h-3.5 flex-shrink-0" />
            {t('dictionary.dictionaryDataFrom')}{' '}
            <a
              href="https://en-word.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 dark:hover:text-gray-300"
            >
              {t('dictionary.openEnglishWordNet')}
            </a>
            {' '}{t('dictionary.ccBy4')}{' '}
            <a
              href="https://en.wiktionary.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 dark:hover:text-gray-300"
            >
              {t('dictionary.wiktionary')}
            </a>
            {' '}{t('dictionary.ccBySa3')}
          </p>
        </div>
      </div>
    </TeacherShell>
  );
}

function Section({ title, action, children }) {
  return (
    <section className="mb-8 last:mb-0">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function AddToCourseListButton({ word, teacherCourseLists }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const [addedToList, setAddedToList] = useState(null);

  const handleAdd = async (list) => {
    setStatus('loading');
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/dictionary/${word.id}/add_to_course_list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ vocabulary_list_id: list.id }),
      });
      if (res.ok) {
        setAddedToList(list.name);
        setStatus('success');
        setTimeout(() => { setOpen(false); setStatus('idle'); }, 2000);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  // Group lists by course
  const grouped = teacherCourseLists.reduce((acc, l) => {
    (acc[l.course_title] = acc[l.course_title] || []).push(l);
    return acc;
  }, {});

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        {t('dictionary.addToClassList')}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-w-[220px] py-1">
          {status === 'success' ? (
            <p className="px-3 py-2 text-sm text-green-600 dark:text-green-400">
              {t('dictionary.addedTo')} &ldquo;{addedToList}&rdquo;
            </p>
          ) : (
            Object.entries(grouped).map(([courseTitle, courseLists]) => (
              <div key={courseTitle}>
                <p className="px-3 py-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                  {courseTitle}
                </p>
                {courseLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => handleAdd(list)}
                    disabled={status === 'loading'}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {list.name}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ExampleSentenceList({ sentences }) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-3">
      {sentences.map((sentence) => (
        <li key={sentence.id} className="flex items-start">
          <div className="flex-1">
            <p className="text-gray-700 dark:text-gray-300 italic">
              &ldquo;{sentence.text}&rdquo;
            </p>
            <div className="flex items-center gap-2 mt-1">
              {sentence.source === 'ai_generated' && (
                <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                  {t('dictionary.aiGenerated')}
                </span>
              )}
              {sentence.attribution && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  &#x2014; {sentence.attribution}
                </span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function RelatedWordsSection({ title, words, colorClass }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {words.map((word) => (
          <Link
            key={word.id}
            href={`/teacher/dictionary/${word.id}`}
            className={cn('px-3 py-1 text-sm rounded-full transition-colors', colorClass)}
          >
            {word.lemma}
          </Link>
        ))}
      </div>
    </div>
  );
}
